import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import RefreshIcon from '@material-ui/icons/Refresh'
import SettingsIcon from '@material-ui/icons/Settings'
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import VolumeOffIcon from '@material-ui/icons/VolumeOff'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import ExplainVisual from './ExplainVisual'
import { observeAndFitMath } from '../../helpers/fitMathToWidth'
import {
  EXPLAIN_SOLUTION_VIEW_CARDS,
  EXPLAIN_SOLUTION_VIEW_WORKSHEET
} from '../../helpers/explainNarrationPrefs'
import './ExplainSessionView.css'

const SkipBackIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5.5 6.25c0-.41.34-.75.75-.75h1.25c.41 0 .75.34.75.75v11.5c0 .41-.34.75-.75.75H6.25a.75.75 0 0 1-.75-.75V6.25z"/>
    <path d="M19.2 6.32a1 1 0 0 1 .05 1.41L13.83 12l5.42 4.27a1 1 0 1 1-1.25 1.56l-6.5-5.12a1 1 0 0 1 0-1.56l6.5-5.12a1 1 0 0 1 1.2-.11z"/>
  </svg>
)

const SkipForwardIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16.75 5.5c.41 0 .75.34.75.75v11.5c0 .41-.34.75-.75.75h-1.25a.75.75 0 0 1-.75-.75V6.25c0-.41.34-.75.75-.75h1.25z"/>
    <path d="M4.8 6.32a1 1 0 0 1 1.2.11l6.5 5.12a1 1 0 0 1 0 1.56l-6.5 5.12a1 1 0 1 1-1.25-1.56L10.17 12 4.75 7.73a1 1 0 0 1 .05-1.41z"/>
  </svg>
)

const FinishCheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M5 12.5l4.2 4.2L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const ConversationIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M3.75 6.25A3.25 3.25 0 0 1 7 3h6.5A3.25 3.25 0 0 1 16.75 6.25v3A3.25 3.25 0 0 1 13.5 12.5H9.4L6 15v-2.5A3.25 3.25 0 0 1 3.75 9.25v-3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M8.5 11.75A3.25 3.25 0 0 1 11.75 8.5H17A3.25 3.25 0 0 1 20.25 11.75v3.25A3.25 3.25 0 0 1 17 18.25h-1.35L12.5 21v-2.75A3.25 3.25 0 0 1 9.25 15V11.75"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Only one player is mounted at a time (StudyRoom picks the inline desktop
// mount or the mobile dock, never both), but the ids that wire the settings
// button to its panel have to be unique even if that ever changes.
let voicePanelSeq = 0

/**
 * Playback controls. Worksheet view has no per-step explanation, so that text
 * sits in a box directly above the controls. Step cards already carry it, so
 * the box stays off in that view; a clipped live region still announces the
 * current explanation when the step changes.
 * StudyRoom mounts this in the fixed dock on both desktop and mobile, so
 * everything it needs must arrive through props rather than through
 * `.explain-session` scoped CSS.
 */
export const ExplainPlayerControls = ({
  currentStepIndex,
  totalSteps,
  explanation = '',
  askEnabled,
  finishing,
  speechSupported = false,
  ttsEnabled = false,
  ttsVoices = [],
  ttsVoiceUri = '',
  onPrevious,
  onNext,
  onFinish,
  onAsk,
  onToggleTts,
  onChangeTtsVoice,
  solutionView = EXPLAIN_SOLUTION_VIEW_CARDS,
  onChangeSolutionView
}) => {
  const isLastStep = totalSteps > 0 && currentStepIndex >= totalSteps - 1
  // Finishing is the completeExplainSession round-trip. After that the student
  // can hide the celebration modal and keep using playback, including Ask.
  const playbackLocked = finishing
  const askLocked = playbackLocked || !askEnabled

  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsWrapRef = useRef(null)
  const settingsButtonRef = useRef(null)
  const voiceSelectRef = useRef(null)
  const solutionViewFocusRef = useRef(null)
  const panelIdRef = useRef(null)
  if (panelIdRef.current === null) {
    voicePanelSeq += 1
    panelIdRef.current = `explain-voice-panel-${voicePanelSeq}`
  }
  const panelId = panelIdRef.current

  const closeSettings = useCallback((returnFocus) => {
    setSettingsOpen(false)
    // Escape and the toggle button hand focus back; an outside click does not,
    // because the click has already moved focus somewhere deliberate.
    if (returnFocus) settingsButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!settingsOpen) return undefined

    // The first control in the panel takes focus so the panel is operable from
    // the keyboard the moment it opens, and Escape has something to return
    // focus from. Narration is second, and absent on a device with no speech.
    const focusTarget = solutionViewFocusRef.current || voiceSelectRef.current
    focusTarget?.focus()

    const handleOutside = (event) => {
      if (settingsWrapRef.current?.contains(event.target)) return
      setSettingsOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return
      // Document fires before window, so this also stops the Ask modal's own
      // Escape handler from reacting to a keystroke meant for this panel.
      event.stopPropagation()
      closeSettings(true)
    }

    document.addEventListener('mousedown', handleOutside)
    document.addEventListener('touchstart', handleOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      document.removeEventListener('touchstart', handleOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [settingsOpen, closeSettings])

  // Finishing locks every control, including this panel. The solution-view
  // choice stays available on a device with no speech engine.
  useEffect(() => {
    if (playbackLocked) setSettingsOpen(false)
  }, [playbackLocked])

  const selectedVoiceUri = ttsVoices.some((voice) => voice.uri === ttsVoiceUri)
    ? ttsVoiceUri
    : (ttsVoices[0]?.uri || '')
  const worksheetSelected = solutionView === EXPLAIN_SOLUTION_VIEW_WORKSHEET
  const stepPositionLabel = totalSteps > 0
    ? `Step ${Math.min(currentStepIndex + 1, totalSteps)} of ${totalSteps}`
    : 'This step'

  return (
    <div className="explain-player-container">
      {worksheetSelected ? (
        <div className="explain-player-explanation">
          <div className="explain-player-explanation-label">{stepPositionLabel}</div>
          <p className="explain-player-explanation-text" aria-live="polite">
            {explanation}
          </p>
        </div>
      ) : (
        // Cards already show this prose. The node stays in the tree, clipped,
        // so a step change is still announced while focus is on the controls.
        <p className="explain-player-explanation-live" aria-live="polite">
          {explanation}
        </p>
      )}

      <div className="explain-player" role="group" aria-label="Lesson playback">
        <div className="explain-player-utility" ref={settingsWrapRef}>
          <button
            type="button"
            ref={settingsButtonRef}
            className={`explain-player-btn utility${settingsOpen ? ' active' : ''}`}
            onClick={() => (settingsOpen ? closeSettings(false) : setSettingsOpen(true))}
            disabled={playbackLocked}
            aria-haspopup="dialog"
            aria-expanded={settingsOpen}
            aria-controls={panelId}
            aria-label="Settings"
            title="Settings"
          >
            <SettingsIcon fontSize="inherit" />
          </button>
          {settingsOpen && (
            <div
              className="explain-voice-panel"
              id={panelId}
              role="dialog"
              aria-label="Settings"
            >
              <fieldset className="explain-settings-fieldset">
                <legend className="explain-voice-panel-label">Solution view</legend>
                <div className="explain-settings-options">
                  <label className="explain-settings-option">
                    <input
                      ref={worksheetSelected ? undefined : solutionViewFocusRef}
                      type="radio"
                      name={`${panelId}-solution-view`}
                      value={EXPLAIN_SOLUTION_VIEW_CARDS}
                      checked={!worksheetSelected}
                      onChange={() => onChangeSolutionView?.(EXPLAIN_SOLUTION_VIEW_CARDS)}
                    />
                    Step cards
                  </label>
                  <label className="explain-settings-option">
                    <input
                      ref={worksheetSelected ? solutionViewFocusRef : undefined}
                      type="radio"
                      name={`${panelId}-solution-view`}
                      value={EXPLAIN_SOLUTION_VIEW_WORKSHEET}
                      checked={worksheetSelected}
                      onChange={() => onChangeSolutionView?.(EXPLAIN_SOLUTION_VIEW_WORKSHEET)}
                    />
                    Worksheet
                  </label>
                </div>
              </fieldset>
              {speechSupported && (
                <>
                  <label className="explain-voice-panel-label" htmlFor={`${panelId}-select`}>
                    Narration voice
                  </label>
                  <select
                    id={`${panelId}-select`}
                    ref={voiceSelectRef}
                    className="explain-voice-panel-select"
                    value={selectedVoiceUri}
                    onChange={(event) => onChangeTtsVoice?.(event.target.value)}
                    disabled={ttsVoices.length === 0}
                  >
                    {ttsVoices.length === 0 ? (
                      <option value="">Loading voices…</option>
                    ) : (
                      ttsVoices.map((voice) => (
                        <option key={voice.uri} value={voice.uri}>
                          {voice.label}
                        </option>
                      ))
                    )}
                  </select>
                  <p className="explain-voice-panel-hint">
                    {ttsEnabled
                      ? 'Narration plays automatically on each step.'
                      : 'Narration is muted. Unmute to hear each step.'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="explain-player-btn skip"
          onClick={onPrevious}
          disabled={currentStepIndex === 0 || playbackLocked}
          aria-label="Previous step"
        >
          <SkipBackIcon />
        </button>

        <button
          type="button"
          className="explain-player-btn ask"
          onClick={onAsk}
          disabled={askLocked}
          aria-label={askEnabled ? 'Ask the AI tutor about this step' : 'AI chat helper is turned off'}
          title={askEnabled ? 'Ask about this step' : 'The AI chat helper is turned off for this account'}
        >
          <ConversationIcon />
        </button>

        {isLastStep ? (
          <button
            type="button"
            className="explain-player-btn finish"
            onClick={onFinish}
            disabled={playbackLocked}
            aria-busy={finishing}
            aria-label={finishing ? 'Finishing session' : 'Finish lesson'}
          >
            {finishing
              ? <span className="explain-player-spinner" aria-hidden="true" />
              : <FinishCheckIcon />}
          </button>
        ) : (
          <button
            type="button"
            className="explain-player-btn skip"
            onClick={onNext}
            disabled={playbackLocked}
            aria-label="Next step"
          >
            <SkipForwardIcon />
          </button>
        )}

        {speechSupported && (
          <div className="explain-player-utility">
            <button
              type="button"
              className={`explain-player-btn utility${ttsEnabled ? ' active' : ''}`}
              onClick={onToggleTts}
              disabled={playbackLocked}
              aria-pressed={!ttsEnabled}
              aria-label={ttsEnabled ? 'Mute narration' : 'Unmute narration'}
              title={ttsEnabled ? 'Mute narration' : 'Unmute narration'}
            >
              {ttsEnabled ? <VolumeUpIcon fontSize="inherit" /> : <VolumeOffIcon fontSize="inherit" />}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export const ExplainFinishModal = ({
  xpAwarded,
  xpAmount,
  onHide,
  onExplainAnother,
  onSwitchToCheckAnswer
}) => (
  <div className="explain-finish-overlay" role="presentation">
    <div
      className="explain-finish-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="explain-finish-title"
    >
      <div className="explain-finish-header">
        <button
          type="button"
          className="explain-paper-icon-btn explain-finish-hide-btn"
          onClick={onHide}
          aria-label="Review steps"
          title="Review steps"
        >
          <SkipPreviousIcon fontSize="inherit" />
        </button>
      </div>
      <div className="study-room-bird-mascot cheering">
        <img src="/favicon.png" alt="" />
      </div>
      <h2 id="explain-finish-title" className="explain-finish-title">Nice work!</h2>
      <p className="explain-finish-subtitle">
        You walked through every step of that problem.
      </p>
      {xpAwarded && xpAmount > 0 && (
        <div className="explain-finish-xp">+{xpAmount} XP earned</div>
      )}
      <div className="explain-finish-actions">
        <button
          type="button"
          className="explain-finish-btn primary"
          onClick={onExplainAnother}
        >
          Explain another problem
        </button>
        <button
          type="button"
          className="explain-finish-btn secondary"
          onClick={onSwitchToCheckAnswer}
        >
          Help me with my answer
        </button>
      </div>
    </div>
  </div>
)

const ExplainMathBlock = ({ content, fontSize, fit }) => {
  const ref = useRef(null)

  useLayoutEffect(() => {
    if (!fit) return undefined
    return observeAndFitMath(ref.current)
  }, [fit, content])

  return (
    <div className="explain-math-block" ref={ref}>
      <MixedContentPreview content={content} fontSize={fontSize} />
    </div>
  )
}

// Only the current step and the ones already worked through are mounted, so a
// step's figure cannot appear before the step does. Memoising on the values
// that drive a step's output keeps an advance to the two steps whose highlight
// changed, plus the newly revealed card.
const ExplainStep = React.memo(({ stepNumber, solution, explanation, visual, state, grouped }) => {
  const isCurrent = state === 'current'
  return (
    <li
      className={`explain-step${isCurrent ? ' current' : ''}`}
      aria-current={isCurrent ? 'step' : undefined}
      aria-label={`Step ${stepNumber}`}
    >
      {!grouped && <div className="explain-step-number">Step {stepNumber}</div>}
      <ExplainMathBlock
        content={solution}
        fontSize={grouped ? '1.25rem' : (isCurrent ? '1.3rem' : '1.15rem')}
        fit={grouped}
      />
      <ExplainVisual visual={visual} compact={grouped} />
      {!grouped && explanation ? (
        <p className="explain-step-explanation">{explanation}</p>
      ) : null}
    </li>
  )
})

const ExplainSessionView = ({
  question,
  topic,
  steps,
  finalAnswer,
  finalVisual = null,
  currentStepIndex,
  explanationText,
  askEnabled,
  ttsEnabled,
  ttsVoices = [],
  ttsVoiceUri = '',
  speechSupported,
  finishing,
  completed,
  errorMessage,
  hideControls = false,
  liveSession = false,
  groupedSolution = false,
  solutionView = EXPLAIN_SOLUTION_VIEW_CARDS,
  onChangeSolutionView,
  onPrevious,
  onNext,
  onFinish,
  onAsk,
  onToggleTts,
  onChangeTtsVoice,
  onStartOver,
  onRefresh,
  reportAnchor = false
}) => {
  const stepsListRef = useRef(null)
  const isFirstRenderRef = useRef(true)

  const totalSteps = steps.length
  const isLastStep = currentStepIndex >= totalSteps - 1
  const currentExplanation = steps[currentStepIndex]?.explanation || explanationText || ''

  const revealedCount = Math.min(
    steps.length,
    Math.max(0, currentStepIndex) + 1
  )
  const revealedSteps = steps.slice(0, revealedCount)

  useEffect(() => {
    // Steps the student has not reached are not mounted, so the step they just
    // moved to is the one that has to come into view. The initial paint is
    // left alone so opening a session does not yank the page down.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    stepsListRef.current?.children[currentStepIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    })
  }, [currentStepIndex])

  return (
    <div className={`explain-session explain-teaching${liveSession ? ' explain-live' : ''}`}>
      <div className={`explain-teaching-layout${!hideControls ? ' has-inline-player' : ''}`}>
        {/* Source order is solution first so the single-column stack reads
            problem, then steps, then controls. The onboarding demo still
            splits those columns on desktop; the live session docks the
            player under the paper instead. */}
        <div className="explain-teaching-solution">
          <div
            className="explain-paper"
            {...(reportAnchor ? { 'data-report-exercise': 'true' } : {})}
          >
            <div className="explain-paper-header">
              {liveSession ? (
                <button
                  type="button"
                  className="explain-paper-icon-btn"
                  onClick={onRefresh}
                  disabled={finishing || completed || !onRefresh}
                  aria-label="Try a different solution"
                  title="Try a different solution"
                >
                  <RefreshIcon fontSize="inherit" />
                </button>
              ) : null}
              <div className="explain-paper-heading">
                <span className="explain-paper-eyebrow">
                  {liveSession ? "Let's solve this" : "Let's work through this"}
                </span>
                {topic && <span className="explain-topic-tag">{topic}</span>}
              </div>
              <button
                type="button"
                className={liveSession ? 'explain-paper-icon-btn' : 'explain-start-over-btn'}
                onClick={onStartOver}
                disabled={finishing}
                aria-label="Start over"
                title="Start over"
              >
                {liveSession ? <CloseIcon fontSize="inherit" /> : 'Start over'}
              </button>
            </div>

            <div className="explain-problem">
              {!liveSession && <div className="explain-block-label">Problem</div>}
              <ExplainMathBlock content={question} fontSize="1.35rem" fit={liveSession || groupedSolution} />
            </div>

            <ol
              className={`explain-steps${groupedSolution ? ' grouped' : ''}`}
              ref={stepsListRef}
              aria-label="Solution steps"
            >
              {revealedSteps.map((step, index) => (
                <ExplainStep
                  key={index}
                  stepNumber={index + 1}
                  solution={step.solution}
                  explanation={step.explanation}
                  visual={step.visual}
                  grouped={groupedSolution}
                  state={index === currentStepIndex ? 'current' : 'past'}
                />
              ))}
            </ol>

            {isLastStep && finalAnswer && (
              <div className="explain-final-answer">
                <div className="explain-block-label">Final answer</div>
                <ExplainMathBlock content={finalAnswer} fontSize="1.35rem" fit={liveSession || groupedSolution} />
                <ExplainVisual visual={finalVisual} compact={groupedSolution} />
              </div>
            )}
          </div>
        </div>

        <div className="explain-teaching-stage">
          {errorMessage && (
            <div className="explain-session-error" role="alert">{errorMessage}</div>
          )}

          {!hideControls && (
            <ExplainPlayerControls
              currentStepIndex={currentStepIndex}
              totalSteps={totalSteps}
              explanation={currentExplanation}
              askEnabled={askEnabled}
              finishing={finishing}
              completed={completed}
              speechSupported={speechSupported}
              ttsEnabled={ttsEnabled}
              ttsVoices={ttsVoices}
              ttsVoiceUri={ttsVoiceUri}
              onPrevious={onPrevious}
              onNext={onNext}
              onFinish={onFinish}
              onAsk={onAsk}
              onToggleTts={onToggleTts}
              onChangeTtsVoice={onChangeTtsVoice}
              solutionView={solutionView}
              onChangeSolutionView={onChangeSolutionView}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ExplainSessionView
